import React, { useMemo, useState } from 'react';
import { useDebouncedApiEffect } from '../../../hooks/useDebouncedApiEffect';
import { ArticulationRegistrationList, GetArticulationOptions } from '../../../services/ReRegistrationService';
import { getAllCampus } from '../../../services/StudentHubService';
import { getSelectableCampusList, getSelectableLearningMaterialsList, isDisabled, isSubmitted, useSubmittedReadOnlyData } from '../../../utils/Articulation-Util';
import ArticulationReviewAndSubmit from './ArticulationReviewAndSubmit';
import ArticulationSelectQualification from './ArticulationSelectQualification';

/**
 * Provides data to the 2 articulation options input steps.
 * @param {object} params
 * @returns 
 */
const ArticulationFormWrapper = ({
    articulationOptionSelected,
    articulationReadOnly,
    currentStep,
    pmcId,
    setArticulationOption,
    setCurrentStep,
    qualificationDetails,
    payloadData
}) => {
    const [learningMaterialsOptions, setLearningMaterialsOptions] = useState([]);
    const [campusOptions, setCampusOptions] = useState([]);
    const [schoolTypes, setSchoolTypes] = useState([]);
    const [_isLoading, _setIsLoading] = useState({ inputData: false, articulationOptions: true });
    const [articulationOptions, setArticulationOptions] = useState([]);
    const isLoading = _isLoading.inputData || _isLoading.articulationOptions;
    const disabled = isDisabled(payloadData.registration);
    const submitted = isSubmitted(payloadData.registration);

    useSubmittedReadOnlyData(submitted, articulationReadOnly, articulationOptions, setArticulationOption);

    const setIsLoading = (key, loading) => {
        _setIsLoading((prevState) => ({ ...prevState, [key]: loading }));
    };

    // Infer learning materials list:
    const learningMaterialsList = useMemo(() => getSelectableLearningMaterialsList(learningMaterialsOptions, articulationOptionSelected),
        [learningMaterialsOptions, articulationOptionSelected]);

    // Infer campus list:
    const campusList = useMemo(() => getSelectableCampusList(campusOptions, articulationOptionSelected),
        [campusOptions, articulationOptionSelected]);

    const getInputData = async () => {
        if (schoolTypes.length > 0 && learningMaterialsList.length > 0 && campusList.length > 0) {
            // No need to fetch again:
            return;
        }
        setIsLoading('inputData', true);
        try {
            const [res1, res2] = await Promise.all([ArticulationRegistrationList(), getAllCampus()]);
            if (res1?.data) {
                setLearningMaterialsOptions(res1.data.learning_method_list ?? []);
                setSchoolTypes(res1.data.school_type_list ?? []);
            }
            if (res2?.data?.resource?.data) {
                const items = res2.data.resource.data?.map(item => {
                    return { label: item.marketing_name_short, value: item.slug, color: item.color }
                }) ?? [];
                setCampusOptions(items);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading('inputData', false);
        }
    };

    const fetchArticulationOptions = async () => {
        if (articulationOptions.length > 0 && !pmcId) {
            // No need to fetch again:
            return;
        }
        setIsLoading('articulationOptions', true);
        try {
            const res = await GetArticulationOptions(pmcId);

            if (res.data) {
                const allOptions = (res.data.responseData ?? []).map((item, index) => ({
                    ...item,
                    id: index + 1
                }));
                setArticulationOptions(allOptions);
            }
        } catch (error) {
            console.error("Error fetching articulation options:", error);
        } finally {
            setIsLoading('articulationOptions', false);
        }
    };

    useDebouncedApiEffect(fetchArticulationOptions, [pmcId]);
    useDebouncedApiEffect(getInputData, []);

    return (
        <>
            {currentStep === 0 ? (
                <ArticulationSelectQualification
                    articulationOptionSelected={articulationOptionSelected}
                    articulationOptions={articulationOptions}
                    campusList={campusOptions}
                    isLoading={isLoading}
                    learningMaterialsList={learningMaterialsList}
                    schoolTypes={schoolTypes}
                    setArticulationOption={setArticulationOption}
                    setCurrentStep={setCurrentStep}
                />
            ) : ((articulationOptionSelected || articulationReadOnly) && (
                <ArticulationReviewAndSubmit
                    articulationReadOnly={articulationReadOnly}
                    articulationOptionSelected={articulationOptionSelected}
                    campusList={campusList}
                    disabled={disabled}
                    learningMaterialsList={learningMaterialsList}
                    payloadData={payloadData}
                    qualificationDetails={qualificationDetails}
                    schoolTypes={schoolTypes}
                    setCurrentStep={setCurrentStep}
                    submitted={submitted}
                />
            ))}
            <br />
            <br />
            <br />
            <br />
        </>
    );
};

export default ArticulationFormWrapper;
